import { useState, useEffect } from 'react';
import { useAuth } from './context/AuthContext';
import { AppLayout } from './layout/AppLayout';
import { LoginPage } from './pages/LoginPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { OrgPickerPage } from './pages/OrgPickerPage';
import { OverviewPage } from './pages/OverviewPage';
import { ProductsPage } from './pages/ProductsPage';
import { MembersPage } from './pages/MembersPage';
import { BillingPage } from './pages/BillingPage';
import { SettingsPage } from './pages/SettingsPage';
import { ActivityPage } from './pages/ActivityPage';
import { AccountPage } from './pages/AccountPage';
import { AdminPortalPage } from './pages/AdminPortalPage';
import { InvitationPage, PENDING_INVITATION_KEY } from './pages/InvitationPage';
import { CheckoutPage } from './pages/CheckoutPage';
import { SignupPage } from './pages/SignupPage';
import { Loader } from './components/Loader';

export function App() {
  const { session, activeOrg, loading } = useAuth();
  const [currentPath, setCurrentPath] = useState<string>(() => window.location.pathname || '/');
  const pendingInvitation = sessionStorage.getItem(PENDING_INVITATION_KEY);

  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname || '/');
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigate = (path: string) => {
    window.history.pushState({}, '', path);
    setCurrentPath(path);
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-canvas)' }}>
        <Loader size={36} label="Verifying your CYRN SSO session…" />
      </div>
    );
  }

  // Checkout handles signed-out visitors itself: it sends them through SSO or
  // sign-up and back, with the chosen plan preserved (SPEC-07 §4.1).
  if (currentPath === '/checkout' || currentPath === '/checkout/return' || currentPath === '/verify-email') {
    return <CheckoutPage />;
  }

  // Account creation exists only here; CYRN Auth has registration disabled.
  if (currentPath === '/signup') {
    return <SignupPage />;
  }

  // Invitation links are handled before the login gate: the recipient is
  // normally signed out, and may not have an account at all yet.
  const inviteMatch = currentPath.match(/^\/invite\/(.+)$/);
  if (inviteMatch) {
    return <InvitationPage token={decodeURIComponent(inviteMatch[1])} onDone={navigate} />;
  }

  // Signing in to accept an invitation returns to "/" with the token parked in
  // session storage, because the OIDC callback has no way to carry it back.
  if (session && pendingInvitation) {
    return <InvitationPage token={pendingInvitation} onDone={navigate} />;
  }

  // If not authenticated, show LoginPage
  if (!session) {
    return <LoginPage />;
  }

  const isPlatformAdmin = Boolean(session.principal.is_platform_admin);

  // Platform admin console lives outside tenant scope: reachable with zero memberships
  // and without an active organisation selected.
  if (currentPath.startsWith('/admin')) {
    if (!isPlatformAdmin) {
      return (
        <div style={{ minHeight: '100vh', background: 'var(--bg-canvas)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-6)' }}>
          <div style={{ maxWidth: 420, textAlign: 'center' }}>
            <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--fw-semibold)', marginBottom: 8 }}>
              Platform admin access required
            </h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)', marginBottom: 20 }}>
              Your account does not carry the CYRN platform administrator privilege.
            </p>
            <button
              onClick={() => navigate('/')}
              style={{
                padding: '9px 16px',
                background: 'var(--accent)',
                color: 'var(--text-on-accent)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--fw-medium)',
              }}
            >
              Back to workspace
            </button>
          </div>
        </div>
      );
    }
    return (
      <AppLayout currentPath={currentPath} onNavigate={navigate}>
        <AdminPortalPage onNavigate={navigate} />
      </AppLayout>
    );
  }

  // Platform admins with no tenant membership belong in the admin console, not onboarding.
  if (isPlatformAdmin && session.memberships.length === 0 && currentPath !== '/onboarding') {
    return (
      <AppLayout currentPath="/admin" onNavigate={navigate}>
        <AdminPortalPage onNavigate={navigate} />
      </AppLayout>
    );
  }

  // If user has zero memberships, redirect to onboarding flow
  if (session.memberships.length === 0 || currentPath === '/onboarding') {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-canvas)', padding: 'var(--space-6)' }}>
        <OnboardingPage
          onSuccess={() => navigate('/')}
          onCancel={session.memberships.length > 0 ? () => navigate('/') : undefined}
        />
      </div>
    );
  }

  // If org picker is selected or no active org set
  if (currentPath === '/org-picker' || !activeOrg) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-canvas)', padding: 'var(--space-6)' }}>
        <OrgPickerPage
          onSelect={() => navigate('/')}
          onCreateNew={() => navigate('/onboarding')}
        />
      </div>
    );
  }

  // Render main app layout for authenticated user with active org
  let pageContent = <OverviewPage onNavigate={navigate} />;

  switch (currentPath) {
    case '/products':
      pageContent = <ProductsPage />;
      break;
    case '/members':
      pageContent = <MembersPage />;
      break;
    case '/billing':
      pageContent = <BillingPage />;
      break;
    case '/settings':
      pageContent = <SettingsPage />;
      break;
    case '/activity':
      pageContent = <ActivityPage />;
      break;
    case '/account':
      pageContent = <AccountPage />;
      break;
    case '/':
    default:
      pageContent = <OverviewPage onNavigate={navigate} />;
      break;
  }

  return (
    <AppLayout currentPath={currentPath} onNavigate={navigate}>
      {pageContent}
    </AppLayout>
  );
}
